<script setup>
import { storeToRefs } from 'pinia'
import Calendar from 'assets/icons/calendar.svg'
import { useMessageStore } from '~/stores/messages'

// Props
const props = defineProps({
    meta: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                type: rawValue => rawValue.type === undefined || typeof rawValue.type === 'string',
                dateText: rawValue => rawValue.dateText === undefined || typeof rawValue.dateText === 'string',
                ical: rawValue => rawValue.ical === undefined || typeof rawValue.ical === 'string',
                location: rawValue => rawValue.location === undefined || typeof rawValue.location === 'string',
                myceExclusive: rawValue => rawValue.myceExclusive === undefined || typeof rawValue.myceExclusive === 'boolean',
                timeText: rawValue => rawValue.timeText === undefined || typeof rawValue.timeText === 'string'
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Get date
const isNews = computed(() => props.meta?.type === 'news')
const isEvent = computed(() => props.meta?.type === 'event')

const getNewsDate = computed(() => props.meta.dateText)

// const getEventDate = computed(() => props.meta?.timeText ? `${props.meta?.dateText} ${staticKeys.value?.general_clock}`.replace('%s', props.meta?.timeText) : props.meta?.dateText);

const getEventDate = computed(() => {
    if (!props.meta?.timeText) {
        return props.meta?.dateText
    }

    return `${props.meta?.dateText} ${props.meta?.timeText} ${staticKeys.value?.general_clock}`
})

const getContentPageBadge = computed(() => {
    return props.meta?.myceExclusive
        ? staticKeys.value?.myce_exclusive_tag_news_event
        : isNews.value
            ? staticKeys.value?.content_type_news
            : staticKeys.value?.content_type_event
})
</script>

<template>
    <aside class="content-page-meta">
        <div class="content-page-meta-summary">
            <div class="content-page-meta-summary-info">
                <h3>
                    {{ meta.location }}
                </h3>

                <p v-if="isNews">
                    {{ getNewsDate }}
                </p>

                <p v-if="isEvent">
                    <NuxtLink v-if="meta.ical" :to="meta.ical" class="content-page-meta-save-event-link">
                        {{ getEventDate }}
                    </NuxtLink>
                    <template v-else>
                        {{ getEventDate }}
                    </template>
                </p>
            </div>

            <div
                v-if="isEvent && meta.ical"
                class="content-page-meta-summary-action"
                :title="staticKeys?.content_savetocalendar"
            >
                <NuxtLink :to="meta.ical" class="content-page-meta-add-to-calendar">
                    <Calendar />
                </NuxtLink>
            </div>
        </div>

        <div class="content-page-meta-badge">
            {{ getContentPageBadge }}
        </div>
    </aside>
</template>

<style lang="scss">
.content-page-meta {
    margin: 30px 0;
}

.content-page-meta-summary {
    color: $content-page-details;
    text-transform: uppercase;
    display: flex;
    width: 100%;
    justify-content: space-between;
    flex-flow: row wrap;

    h3,
    p {
        margin: 0;

        @include body1-extrabold;

        @include bp(medium1) {
            @include h3-extrabold;
        }
    }

    h3 {
        @include bp(large1) {
            margin-block-end: 10px;
        }
    }
}

.content-page-meta-summary-info {
    width: calc(100% - 60px);
}

.content-page-meta-add-to-calendar {
    background: $add-to-calendar-custom-btn-bg;
    padding: 10px;
    border-radius: 10px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    svg {
        width: 24px;
        height: 24px;
        display: block;
        fill: $add-to-calendar-custom-btn-icon;

        @include bp(medium1) {
            width: 32px;
            height: 32px;
        }
    }
}

.content-page-meta-save-event-link {
    color: inherit;
    cursor: pointer;
    text-decoration: underline;
}

.content-page-meta-badge {
    padding: 8px 20px;
    border-radius: 32px;
    background: $content-page-badge-bg;
    display: inline-block;
    font-weight: 800;
    font-size: px2rem(14);
    line-height: 1;
    margin-block: px2rem(30);

    @include bp(medium1) {
        font-size: px2rem(20);
    }
}
</style>
